import { cloneElement, isValidElement, useMemo } from "react";

import { Icon } from "@blueprintjs/core";
import { BlueprintIcons_16Id } from "@blueprintjs/icons/lib/esm/generated/16px/blueprint-icons-16";
import { Column, ColumnDef, Row } from "@tanstack/react-table";
import { Link } from "react-router";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import {
  isProgramLead,
  isWorkspaceAdmin,
} from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { buildComponentMetadata } from "@/components/pages_logged_in/program/components/componentMetadata";
import { COMPONENT_STATUS_STYLES } from "@/components/pages_logged_in/program/components/constants";
import { getProgramComponentPath } from "@/components/pages_logged_in/program/components/get-program-component-path";
import { NO_RESPONSE_COMPONENT_TYPES } from "@/components/pages_logged_in/program/components/program-details/const";
import { StatusBadge } from "@/components/pages_logged_in/program/components/StatusBadge";
import {
  calculateIntroStats,
  calculateRegularSessionStats,
  isCloseEarlyComponent,
  ONE_ONE_COACHING_STAGE_STYLES,
  roundToOneDecimalPlace,
} from "@/components/pages_logged_in/program/components/utils";
import { FlexibleButton } from "@/components/pieces/button/FlexibleButton";
import { DateTimeDisplay } from "@/components/pieces/DateTimeDisplay";
import { Avatar } from "@/components/pieces/users/Avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  ComponentStatus,
  ComponentTypeEnum,
  ProgramStatus,
  ProgramUserRole,
} from "@/graphql";
import {
  CoachingStage,
  CoachParticipant,
  ComponentParticipant,
  MainComponent,
  MainProgram,
  ProgramParticipant,
} from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";
import { Resolve } from "@/utils/types";

export type Participant = Resolve<
  (ComponentParticipant | ProgramParticipant | CoachParticipant) & {
    participantCompletionStatus?: ComponentStatus | null;
    stage?: CoachingStage | null;
    selectedCoach?: ComponentParticipant["selectedCoach"];
    coach?: ComponentParticipant["selectedCoach"];
    meetings?: CoachParticipant["meetings"];
    programComponents?: ComponentParticipant["programComponents"];
    deleted?: boolean | null;
    creditsUsed?: string;
    programComponentResponseId?: ComponentParticipant["programComponentResponseId"];
    isWorkspaceMemberActive?: ProgramParticipant["isWorkspaceMemberActive"];
    pendingStatus?: string;
    roles?: ComponentParticipant["roles"];
    completedAt?: ComponentParticipant["completedAt"];
    lastReminderAt?: ComponentParticipant["lastReminderAt"];
    totalCreditsUsed?: ComponentParticipant["totalCreditsUsed"];
  }
>;

interface UseParticipantTableColumnsProps {
  component?: MainComponent;
  program: MainProgram;
  onNudgeClick: (participant: Participant) => void;
}

interface RenderUserCellProps {
  component?: MainComponent;
  program?: MainProgram;
  canViewResponses?: boolean;
}

export const renderUserCell = (
  row: Row<Participant>,
  options?: RenderUserCellProps,
) => {
  const { component, program, canViewResponses } = options || {};
  const { user, deleted, isWorkspaceMemberActive, programComponentResponseId } =
    row.original;

  const isAbleToClickResponse = canViewResponses && programComponentResponseId;
  const WrapperComp = isAbleToClickResponse ? Link : Box;
  const resultPath = isAbleToClickResponse
    ? getProgramComponentPath({
        type: component?.type,
        programId: program?.id,
        componentId: component?.id,
        responseId: programComponentResponseId,
      })
    : "#";

  return (
    <WrapperComp
      to={resultPath}
      style={{
        fontWeight: isAbleToClickResponse ? "600" : undefined,
      }}
    >
      <Flex alignItems="center" style={{ gap: 8 }}>
        <Box className="flex-shrink-0">
          <Tooltip>
            <TooltipTrigger>
              <Avatar size={20} user={user} />
            </TooltipTrigger>
            <TooltipContent>{user?.email}</TooltipContent>
          </Tooltip>
        </Box>

        <Box
          style={{
            whiteSpace: "normal",
          }}
        >
          <span
            style={{
              color:
                !deleted && isWorkspaceMemberActive === false
                  ? SUBTEXT_COLOR
                  : undefined,
            }}
          >
            {user?.name || user?.email || ""} {deleted ? " (Removed)" : ""}
          </span>
          {!deleted && isWorkspaceMemberActive === false && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Icon
                  icon="info-sign"
                  size={16}
                  color={SUBTEXT_COLOR}
                  style={{ marginLeft: 5 }}
                />
              </TooltipTrigger>
              <TooltipContent>User removed from workspace</TooltipContent>
            </Tooltip>
          )}
        </Box>
      </Flex>
    </WrapperComp>
  );
};

export const renderStatusCell = (
  row: Row<Participant>,
  options?: {
    isCloseEarly: boolean;
  },
) => {
  const { isCloseEarly } = options || {};
  const { participantCompletionStatus } = row.original;
  const key = participantCompletionStatus;
  const componentStatusStyle = key && COMPONENT_STATUS_STYLES[key];

  const color = isCloseEarly
    ? SUBTEXT_COLOR
    : componentStatusStyle?.textColor || componentStatusStyle?.color;
  const icon = componentStatusStyle?.icon;

  // Clone the icon element with the dynamic color
  const iconWithColor =
    icon && isValidElement(icon)
      ? cloneElement(icon as React.ReactElement<{ color: string }>, { color })
      : icon;

  return (
    <Flex alignItems="center">
      {iconWithColor}
      <Box fontSize={14} fontWeight={500} ml="5px" color={color}>
        {componentStatusStyle?.text}
      </Box>
    </Flex>
  );
};

export const useParticipantTableColumns = ({
  component,
  program,
  onNudgeClick,
}: UseParticipantTableColumnsProps) => {
  const isAdmin = isWorkspaceAdmin();
  const isLead = isProgramLead(program?.id);
  // const isProgramClosed = Boolean(program?.closedAt);
  const isProgramLive = program?.status === ProgramStatus.Live;
  const canViewResponses = component?.type
    ? !NO_RESPONSE_COMPONENT_TYPES.includes(component.type)
    : false;

  const metaData = buildComponentMetadata({
    component,
    isProgramLead: isLead,
  });

  const isCloseEarly = component ? isCloseEarlyComponent(component) : false;

  const renderActions = (row: Row<Participant>) => {
    const {
      roles,
      deleted,
      participantCompletionStatus,
      programComponentResponseId,
    } = row.original || {};
    const isParticipant = roles?.includes(ProgramUserRole.Participant);
    const isAbleToClickResponse =
      canViewResponses && programComponentResponseId;
    const resultPath = isAbleToClickResponse
      ? getProgramComponentPath({
          type: component?.type,
          programId: program.id,
          componentId: component?.id,
          responseId: programComponentResponseId,
        })
      : "#";

    const canNudge =
      !deleted &&
      isProgramLive &&
      participantCompletionStatus &&
      [
        ComponentStatus.NotStarted,
        ComponentStatus.Started,
        ComponentStatus.PastDue,
      ].includes(participantCompletionStatus) &&
      !metaData.isAnnouncement &&
      !metaData.isGroupMeeting &&
      !metaData.isOneOneMaster &&
      (isAdmin || (isParticipant && isLead));
    return (
      <div className="flex items-center gap-2">
        {canNudge && (
          <FlexibleButton
            size="small"
            variant="white"
            onClick={() => {
              onNudgeClick(row.original);
            }}
            style={{ fontSize: 14, padding: "4px 8px" }}
          >
            Nudge
          </FlexibleButton>
        )}

        {isAbleToClickResponse && (
          <FlexibleButton
            as={Link}
            to={resultPath}
            variant="white"
            fill={false}
            size="small"
          >
            View Responses
          </FlexibleButton>
        )}
      </div>
    );
  };

  const nameColumn = useMemo<ColumnDef<Participant>>(() => {
    return {
      id: "name",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Name" />
      ),
      accessorKey: "name",
      cell: ({ row }) => {
        return renderUserCell(row, {
          component,
          program,
          canViewResponses,
        });
      },
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: "Name",
        placeholder: "Search participants",
        variant: "text",
      },
    };
  }, []);

  const actionsColumn = useMemo<ColumnDef<Participant>>(() => {
    return {
      id: "actions",
      header: "Actions",
      isNotSortable: true,
      cell: ({ row }) => {
        return renderActions(row);
      },
    };
  }, []);

  const lastRemindedColumn = useMemo<ColumnDef<Participant>>(() => {
    return {
      id: "lastReminderAt",
      header: "Last Reminded",
      accessorKey: "lastReminderAt",
      cell: ({ row }) => {
        const value = row.original.lastReminderAt;
        return value ? <DateTimeDisplay time={value} /> : "-";
      },
    };
  }, []);

  const statusColumn = useMemo<ColumnDef<Participant>>(() => {
    return {
      id: "participantCompletionStatus",
      accessorKey: "participantCompletionStatus",
      cell: ({ row }) => {
        return renderStatusCell(row, { isCloseEarly });
      },
      header: "Status",
      enableColumnFilter: true,
      enableHiding: false,
    };
  }, []);

  const completedAtColumn = useMemo<ColumnDef<Participant>>(() => {
    return {
      id: "completedAt",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Completion Date" />
      ),
      accessorKey: "completedAt",
      cell: ({ row }) => {
        const value = row.original.completedAt;
        return value ? <DateTimeDisplay time={value} /> : "-";
      },
      enableColumnFilter: false,
      enableSorting: true,
    };
  }, []);

  const regularColumns = useMemo<ColumnDef<Participant>[]>(() => {
    const isShownLastReminded =
      !metaData.isGroupMeeting && !metaData.isAnnouncement;

    const isShownCompletedAt =
      !metaData.isGroupMeeting && !metaData.isAnnouncement;

    const isShownStatus = !metaData.isGroupMeeting && !metaData.isAnnouncement;

    const isShownAction = !metaData.isAnnouncement;

    const columns = [nameColumn];
    if (isShownStatus) {
      columns.push(statusColumn);
    }
    if (isShownCompletedAt) {
      columns.push(completedAtColumn);
    }
    if (isShownLastReminded) {
      columns.push(lastRemindedColumn);
    }
    if (isShownAction) {
      columns.push(actionsColumn);
    }
    return columns;
  }, [metaData]);

  // Because we have different columns for one-on-one coaching, we need to return a different columns array
  const oneOneCoachingColumns = useMemo<ColumnDef<Participant>[]>(() => {
    return [
      nameColumn,
      {
        id: "stage",
        header: "Stage",
        cell: ({ row }) => {
          const stage = row.original.stage || "NotStarted";
          const stageStyle = ONE_ONE_COACHING_STAGE_STYLES[stage];
          const color = isCloseEarly
            ? SUBTEXT_COLOR
            : stageStyle?.textColor || stageStyle?.color;

          let icon = stageStyle?.icon;

          if (isCloseEarly) {
            icon = (
              <Icon
                icon={stageStyle?.iconClass as BlueprintIcons_16Id}
                size={14}
                color={SUBTEXT_COLOR}
              />
            );
          }
          return (
            <Flex alignItems="center">
              {icon}
              <Box fontSize={14} fontWeight={500} ml="5px" color={color}>
                {stageStyle?.text || "Not Started"}
              </Box>
            </Flex>
          );
        },
      },
      {
        id: "coach",
        header: "Coach",
        accessorKey: "selectedCoach",
        enableColumnFilter: true,
        enableSorting: true,
        cell: ({ row }) => {
          const { selectedCoach } = row.original;
          return (
            <Flex alignItems="center">
              {selectedCoach ? (
                <Flex alignItems="center">
                  <Box mr={2}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Avatar size={20} user={selectedCoach} />
                      </TooltipTrigger>
                      <TooltipContent>{selectedCoach?.name}</TooltipContent>
                    </Tooltip>
                  </Box>
                  <Box
                    ml={1}
                    css={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedCoach?.name}
                  </Box>
                </Flex>
              ) : (
                <Box>-</Box>
              )}
            </Flex>
          );
        },
      },
      {
        id: "intro",
        header: ({ column }: { column: Column<Participant, unknown> }) => (
          <DataTableColumnHeader
            column={column}
            title="Intro Sessions"
            className="whitespace-nowrap"
          />
        ),
        cell: ({ row }) => {
          const { programComponents } = row.original;
          const introComponent = programComponents?.find(
            (component) =>
              component?.type === ComponentTypeEnum.OneOneCoachingIntro,
          );
          const isIntroMissed =
            introComponent?.status === ComponentStatus.Missed;
          const components = ensureNonNullArray(programComponents);
          const stats = calculateIntroStats(components);
          const introCount = stats.completedIntroCount || 0;
          let upcomingCount = stats.upcomingIntroCount || 0;
          const isNotFindCoach = !upcomingCount && !introCount;
          if (isIntroMissed) {
            return <StatusBadge status={"Missed"} text={1} />;
          }
          return (
            <>
              {isNotFindCoach ? (
                <>-</>
              ) : (
                <div className="flex gap-2">
                  {!!introCount && (
                    <StatusBadge status={"Completed"} text={introCount} />
                  )}
                  {!!upcomingCount && (
                    <StatusBadge status={"Upcoming"} text={upcomingCount} />
                  )}
                </div>
              )}
            </>
          );
        },
      },
      {
        id: "session",
        header: ({ column }: { column: Column<Participant, unknown> }) => (
          <DataTableColumnHeader
            column={column}
            title="Regular Sessions"
            className="whitespace-nowrap"
          />
        ),
        cell: ({ row }) => {
          const components = ensureNonNullArray(row.original.programComponents);
          const stats = calculateRegularSessionStats(
            components.filter(
              (component) =>
                component.type === ComponentTypeEnum.OneOneCoachingSession,
            ),
          );

          const isNotFindCoach =
            !row.original.selectedCoach &&
            !stats.completedRegularSessionCount &&
            !stats.upcomingRegularSessionCount &&
            !stats.missedRegularSessionCount;

          return (
            <>
              {isNotFindCoach ? (
                <>-</>
              ) : (
                <div className="flex gap-2">
                  {!!stats.completedRegularSessionCount && (
                    <StatusBadge
                      status={"Completed"}
                      text={stats.completedRegularSessionCount}
                    />
                  )}
                  {!!stats.upcomingRegularSessionCount && (
                    <StatusBadge
                      status={"Upcoming"}
                      text={stats.upcomingRegularSessionCount}
                    />
                  )}
                  {!!stats.missedRegularSessionCount && (
                    <StatusBadge
                      status={"Missed"}
                      text={stats.missedRegularSessionCount}
                    />
                  )}
                </div>
              )}
            </>
          );
        },
      },
      {
        id: "totalCreditsUsed",
        header: ({ column }: { column: Column<Participant, unknown> }) => (
          <DataTableColumnHeader
            column={column}
            title="Credits Used"
            className="whitespace-nowrap"
          />
        ),
        accessorKey: "totalCreditsUsed",
        enableSorting: true,
        enableColumnFilter: true,
        cell: ({ row }) => {
          const creditsUsed = row.original.totalCreditsUsed;
          return (
            <div className="flex items-center gap-2">
              <EmbedSvg src={CreditSVG} height={16} width={16} />
              {roundToOneDecimalPlace(creditsUsed || 0)}
            </div>
          );
        },
      },
      actionsColumn,
    ];
  }, [actionsColumn, nameColumn, metaData, isCloseEarly]);

  if (metaData.isOneOneMaster) {
    return {
      columns: oneOneCoachingColumns,
    };
  }
  return {
    columns: regularColumns,
  };
};
